import { ShopigurusHTMLElement } from '@shopigurus/base';

/**
 * ShpfltBatteryGuard — notice rendered by snippets/shpflt-battery-guard.liquid when a heated garment is in the cart
 * without its Easy Gift battery (checkout is disabled in Liquid).
 *
 * Why JS: adding the battery is a Cart API mutation, and the cart drawer / cart page must re-render so the Liquid
 * guard is evaluated again.
 *
 * 1. Each [data-battery-variant-id] button adds the battery exactly like the Easy Gift popup:
 *    POST /cart/add.js?_easygift_internal=true  { items: [{ id, quantity, properties: { _Gifted: <rule name> } }] }
 *    Then the drawer re-renders through the theme "cart:change" event (sections bundled with
 *    "cart:prepare-bundled-sections"), and the cart page reloads (what the theme does after every cart change there).
 * 2. When the cart changes in the background (Easy Gift "afterCartRequest", tab visible again), and a notice is on
 *    the page, the drawer is refreshed ("cart:refresh") and the cart page reloads, but only if the cart really changed.
 *
 * @example
 * <shpflt-battery-guard>
 *   <button type="button" data-battery-variant-id="123" data-battery-quantity="1" data-battery-property="Batería">…</button>
 *   <p data-battery-error hidden></p>
 * </shpflt-battery-guard>
 */

const isCartPage = () => window.themeVariables?.settings?.pageType === 'cart';
const root = () => window.Shopify?.routes?.root ?? '/';

function announce(message) {
  const holder = document.getElementById('status-announcement');
  if (!holder) return;
  holder.textContent = '';
  setTimeout(() => {
    holder.textContent = message;
  }, 100);
}

async function cartSignature() {
  lastOwnFetch = Date.now();
  const cart = await (await fetch(`${root()}cart.js`)).json();
  return cart.items.map((item) => `${item.key}:${item.quantity}`).join('|');
}

// ---------- Background changes (one listener per page, active only while a notice is in the DOM) ----------

let lastSignature = null;
let checking = false;
let lastOwnFetch = 0;

async function recheckGuard() {
  if (checking || !document.querySelector('shpflt-battery-guard')) return;
  checking = true;
  try {
    const signature = await cartSignature();
    if (lastSignature !== null && signature !== lastSignature) {
      if (isCartPage()) {
        window.location.reload();
        return;
      }
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
    lastSignature = signature;
  } catch (_error) {
    // Network error: keep the current state, the next change will retry.
  } finally {
    checking = false;
  }
}

let listenersBound = false;

function bindBackgroundListeners() {
  if (listenersBound) return;
  listenersBound = true;

  // Theme cart changes already re-render the drawer: only keep the signature in sync.
  document.addEventListener('cart:change', (event) => {
    const items = event.detail?.cart?.items;
    if (items) lastSignature = items.map((item) => `${item.key}:${item.quantity}`).join('|');
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') recheckGuard();
  });

  // Easy Gift exposes window.EasyGift.Events once its script has run (it loads async).
  let tries = 0;
  const subscribe = () => {
    const events = window.EasyGift?.Events;
    if (events?.subscribe) {
      events.subscribe('afterCartRequest', () => {
        // Ignore the request we just made ourselves (avoids a fetch loop if Easy Gift reports reads too).
        if (Date.now() - lastOwnFetch < 1500) return;
        setTimeout(recheckGuard, 300);
      });
      return;
    }
    if (++tries < 20) setTimeout(subscribe, 500);
  };
  subscribe();

  recheckGuard();
}

// ---------- Notice ----------

class ShpfltBatteryGuard extends ShopigurusHTMLElement {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick, { signal: this.abortController.signal });
    bindBackgroundListeners();
  }

  #onClick = (event) => {
    const button = event.target.closest('[data-battery-variant-id]');
    if (!button || !this.contains(button) || button.getAttribute('aria-busy') === 'true') return;
    this.#addBattery(button);
  };

  #setBusy(button, busy) {
    this.querySelectorAll('[data-battery-variant-id]').forEach((other) => {
      other.disabled = busy;
    });
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (busy) {
      button.dataset.label = button.textContent;
      button.textContent = this.dataset.adding || button.textContent;
    } else if (button.dataset.label) {
      button.textContent = button.dataset.label;
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-battery-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }

  async #addBattery(button) {
    this.querySelector('[data-battery-error]')?.setAttribute('hidden', '');
    this.#setBusy(button, true);

    const sections = new Set();
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );

    const item = {
      id: Number(button.dataset.batteryVariantId),
      quantity: Number(button.dataset.batteryQuantity) || 1,
      properties: { _Gifted: button.dataset.batteryProperty },
    };

    try {
      const response = await fetch(`${root()}cart/add.js?_easygift_internal=true`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [item], sections: [...sections].join(',') }),
      });
      const json = await response.json();

      if (!response.ok) {
        this.#setBusy(button, false);
        this.#showError(json.description || json.message || this.dataset.error);
        return;
      }

      if (isCartPage()) {
        window.location.reload();
        return;
      }

      const cart = await (await fetch(`${root()}cart.js`)).json();
      cart.sections = json.sections;
      lastSignature = cart.items.map((line) => `${line.key}:${line.quantity}`).join('|');

      const drawer = this.closest('cart-drawer');
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'battery-guard:add', cart } })
      );

      announce((this.dataset.added || '').replace('[battery]', button.dataset.batteryName || ''));

      // The notice (and the focused button) is gone after the re-render: move focus to the checkout button.
      requestAnimationFrame(() => {
        const checkout = drawer?.querySelector('button[name="checkout"]:not([disabled])');
        checkout?.focus();
      });
    } catch (_error) {
      this.#setBusy(button, false);
      this.#showError(this.dataset.error);
    }
  }
}

if (!customElements.get('shpflt-battery-guard')) {
  customElements.define('shpflt-battery-guard', ShpfltBatteryGuard);
}
